import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT, getEnglish } from '../utils/i18n';
import { JsonLd } from '../utils/seo';
type FaqCategory = 'Ordering' | 'Delivery' | 'Materials';
interface FaqItem {
  questionKey: string;
  /** Groups the question under a filter chip. */
  category: FaqCategory;
  answer: string | React.ReactNode;
  answerKey?: string;
  /**
   * Plain-text answer used ONLY for the FAQPage structured data. Required on
   * items whose visible answer is JSX (schema can't carry React nodes).
   * Items using `answerKey` get their schema text from the translation.
   */
  seoAnswer?: string;
}
// Grouped by what the customer is actually trying to settle. Sixteen questions
// in one list buried the answer anyone needed; the chips above the list show
// one group at a time so only ~4 questions are on screen, while the FAQPage
// schema below still carries all sixteen for search.
const faqs: FaqItem[] = [
// --- Ordering ---
{
  questionKey: 'faq.q3',
  category: 'Ordering',
  answerKey: 'faq.a3',
  answer: ''
}, {
  questionKey: 'faq.q15',
  category: 'Ordering',
  answerKey: 'faq.a15',
  answer: ''
}, {
  questionKey: 'faq.q16',
  category: 'Ordering',
  answerKey: 'faq.a16',
  answer: ''
}, {
  questionKey: 'faq.q6',
  category: 'Ordering',
  answerKey: 'faq.a6',
  answer: ''
},
// --- Delivery ---
{
  questionKey: 'faq.q1',
  category: 'Delivery',
  seoAnswer: 'We deliver throughout King and Pierce counties — Bellevue, Kirkland, Redmond, Issaquah, Renton, Seattle, Auburn, Kent, Federal Way, Tacoma, Puyallup, Bonney Lake, and beyond.',
  answer: <>
        We deliver throughout King and Pierce counties — Bellevue, Kirkland,
        Redmond, Issaquah, Renton, Seattle, Auburn, Kent, Federal Way, Tacoma,
        Puyallup, Bonney Lake, and beyond.{' '}
        <Link to="/delivery-zones" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-836" data-mp="mzgbmombs44g">
          View our full delivery zone map →
        </Link>
      </>
}, {
  questionKey: 'faq.q11',
  category: 'Delivery',
  answerKey: 'faq.a11',
  answer: ''
}, {
  questionKey: 'faq.q14',
  category: 'Delivery',
  answerKey: 'faq.a14',
  answer: ''
}, {
  questionKey: 'faq.q13',
  category: 'Delivery',
  answerKey: 'faq.a13',
  answer: ''
}, {
  questionKey: 'faq.q7',
  category: 'Delivery',
  seoAnswer: 'It depends on the situation. In some cases we can spread the material depending on access, terrain, and the type of project. Call us at (206) 650-0129 to discuss your specific setup, and see our Delivery Prep Guide to get your site ready.',
  answer: <>
        It depends on the situation. In some cases we can spread the material
        depending on access, terrain, and the type of project.{' '}
        <a href="tel:206-650-0129" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-837" data-mp="1n5tp8km3od48">
          Give us a call
        </a>{' '}
        to discuss your specific setup. Check out our{' '}
        <Link to="/delivery-prep" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-838" data-mp="2pugdp07mnsh">
          Delivery Prep Guide
        </Link>{' '}
        to get your site ready.
      </>
},
// --- Materials ---
{
  questionKey: 'faq.q8',
  category: 'Materials',
  answerKey: 'faq.a8',
  answer: ''
}, {
  questionKey: 'faq.q9',
  category: 'Materials',
  answerKey: 'faq.a9',
  answer: ''
}, {
  questionKey: 'faq.q10',
  category: 'Materials',
  answerKey: 'faq.a10',
  answer: ''
}, {
  questionKey: 'faq.q17',
  category: 'Materials',
  answerKey: 'faq.a17',
  answer: ''
}, {
  questionKey: 'faq.q18',
  category: 'Materials',
  answerKey: 'faq.a18',
  answer: ''
}, {
  questionKey: 'faq.q4',
  category: 'Materials',
  seoAnswer: 'Use our Material Calculator & Quiz to estimate based on your project dimensions, or compare materials side by side. You can also call us at (206) 650-0129 and we will help you figure it out.',
  answer: <>
        Use our{' '}
        <Link to="/help" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-839" data-mp="1wwwpmcahr7w4">
          Material Calculator & Quiz
        </Link>{' '}
        to estimate based on your project dimensions, or{' '}
        <Link to="/compare" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-840" data-mp="26uj97c2qjnmf">
          compare materials
        </Link>{' '}
        side by side. You can also give us a call and we'll help you figure it
        out.
      </>
},
// Cost of delivery belongs with the rest of the delivery questions; the
// minimum order and payment methods are part of placing an order.
{
  questionKey: 'faq.q12',
  category: 'Delivery',
  answerKey: 'faq.a12',
  answer: ''
}, {
  questionKey: 'faq.q2',
  category: 'Ordering',
  answerKey: 'faq.a2',
  answer: ''
}, {
  questionKey: 'faq.q5',
  category: 'Ordering',
  answerKey: 'faq.a5',
  answer: ''
}];
const CATEGORIES: FaqCategory[] = ['Ordering', 'Delivery', 'Materials'];
export function FAQ() {
  const t = useT();
  const [activeCategory, setActiveCategory] = useState<FaqCategory>('Ordering');
  // Keyed by questionKey rather than list position: filtering changes indices,
  // and an index-based key would leave a different question open when the
  // category switches.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [lastClosedKey, setLastClosedKey] = useState<string | null>(null);
  const visibleFaqs = useMemo(() => faqs.filter(f => f.category === activeCategory), [activeCategory]);
  const toggleFAQ = (key: string) => {
    if (openKey === key) {
      setLastClosedKey(key);
      setOpenKey(null);
    } else {
      setLastClosedKey(null);
      setOpenKey(key);
    }
  };
  return <section className="py-12 sm:py-16 dark:bg-[#202020] transition-colors duration-300 scroll-mt-32 bg-[#F7f5ef]" id="faq" data-id="element-841" data-mp="25e5da8fe5wd5">
      {/* FAQPage markup — these questions match real search queries, so they
          were invisible to Google without it. Every question is included even
          though only one category renders at a time. English text only: the
          schema describes the canonical page. */}
      <JsonLd data={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({
        '@type': 'Question',
        name: getEnglish(f.questionKey),
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.seoAnswer ?? (f.answerKey ? getEnglish(f.answerKey) : '')
        }
      }))
    }} data-id="element-842" data-mp="66go9hpj470t" />
      {/* Header */}
      <div className="mx-auto mb-8 max-w-5xl px-4 sm:mb-10 sm:px-6" data-id="element-843" data-mp="3jdic8n1mqtse">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} data-id="element-844" data-mp="3jdqnybm0nt20">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-baseline md:gap-7" data-id="element-845" data-mp="1hjrgk1c0lvul">
            <h2 className="font-heading text-5xl font-extrabold leading-[1.1] tracking-tight text-stone-900 dark:text-white md:text-6xl lg:text-7xl" data-id="element-846" data-mp="1ckxydvls9go3">
              Da <span className="font-black text-[#04A05C]" style={{
              color: "#059F5F"
            }} data-id="element-847" data-mp="1p4l8eltk9n15">FAQs</span>
            </h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-848" data-mp="1txo2qw65x0ie">— Get answers fast</span>
          </div>
          <p className="max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-849" data-mp="1yxkbt9tpvi4t">
            {t('faq.subtitle')}
          </p>
        </motion.div>
      </div>

      {/* Category filter — same chip pattern as the Materials Hub, so the
          interaction is already familiar by the time customers reach here. */}
      <div className="mx-auto mb-2 max-w-5xl px-4 sm:px-6" data-id="element-850" data-mp="1g5rgh8r6dktx">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="FAQ categories" data-id="element-851" data-mp="2mlaguyd0nnji">
          {CATEGORIES.map(category => {
          const isActive = category === activeCategory;
          const count = faqs.filter(f => f.category === category).length;
          return <button key={category} type="button" role="tab" aria-selected={isActive} onClick={() => {
            setActiveCategory(category);
            setOpenKey(null);
            setLastClosedKey(null);
          }} className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${isActive ? 'bg-[#00754A] text-white' : 'bg-white text-stone-700 hover:bg-stone-100 dark:bg-white/10 dark:text-white dark:hover:bg-white/20'}`} data-id="element-852" data-mp="3cm34a6nvi0hj">
                {category}
                <span className={`ml-2 text-xs font-semibold ${isActive ? 'text-white/70' : 'text-stone-400 dark:text-white/40'}`} data-id="element-853" data-mp="3rcaczuo2w551">
                  {count}
                </span>
              </button>;
        })}
        </div>
      </div>

      {/* FAQ List - Full Width */}
      <div className="w-full" data-id="element-854" data-mp="2ayahcvw9n1nl">
        {visibleFaqs.map((faq, index) => {
        const isOpen = openKey === faq.questionKey;
        const answerContent = faq.answerKey ? t(faq.answerKey) : faq.answer;
        return <motion.div key={faq.questionKey} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: index * 0.04,
          duration: 0.25
        }} className={`w-full transition-colors duration-300 ${isOpen ? 'bg-[#00754A]' : 'bg-transparent'}`} data-id="element-855" data-mp="lipxusluffja" data-mpi={index}>
              <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-856" data-mp="gpnch7r7zce6">
                {/* Row padding sets the height. A fixed pixel height was
                    clipping/misaligning the longer questions, since they wrap
                    to two or three lines on narrow screens. */}
                <button onClick={() => toggleFAQ(faq.questionKey)} className="w-full flex items-start justify-between gap-5 py-5 sm:py-7 text-left group" aria-expanded={isOpen} aria-controls={`faq-answer-${faq.questionKey}`} data-id="element-857" data-mp="3pwogye1d6xru">
                  <span className={`font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold leading-[1.15] transition-colors duration-300 ${isOpen ? 'text-white' : lastClosedKey === faq.questionKey ? 'text-[#04A05C] dark:text-[#04A05C] group-hover:text-[#048036] dark:group-hover:text-[#048036]' : 'text-stone-900 dark:text-white group-hover:text-[#04A05C] dark:group-hover:text-[#04A05C]'}`} data-id="element-858" data-mp="1o51ixsj222lv">
                    {t(faq.questionKey)}
                  </span>
                  <motion.span animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className={`shrink-0 mt-0.5 sm:mt-1 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-859" data-mp="hf3pcl1mb6sb">
                    <Plus className="w-7 h-7 sm:w-9 sm:h-9 stroke-[1.25]" data-id="element-860" data-mp="w3zxei3ocjsx" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-861" data-mp="1087m4fa4tzr5">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" id={`faq-answer-${faq.questionKey}`} data-id="element-862" data-mp="3guqt5g82rmb9">
                      <p className="pb-6 sm:pb-8 text-base sm:text-lg md:text-xl text-white/95 leading-relaxed max-w-3xl font-medium" data-id="element-863" data-mp="3n966z1h3z6uf">
                        {answerContent}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Still Have Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10" data-id="element-864" data-mp="34dg296hsjwcu">
        <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left py-4 gap-3 sm:gap-5" data-id="element-865" data-mp="2zly22knldzsx">
          <MessageCircle className="w-5 h-5 text-[#04A05C] shrink-0" data-id="element-866" data-mp="2bv2393kpy4c6" />
          <p className="text-stone-900 dark:text-white font-heading font-extrabold text-lg sm:text-xl" data-id="element-867" data-mp="26x8wa8bld6au">
            {t('faq.stillQuestions')}
            <span className="font-normal text-stone-600 dark:text-stone-400 text-base ml-2" data-id="element-868" data-mp="2wkcyeup8ro3q">
              {t('faq.shootText')}
            </span>
          </p>
          <a href="sms:206-650-0129" className="inline-flex items-center justify-center px-6 py-2.5 hover:bg-[#048B3A] text-white rounded-full font-bold text-sm transition-colors bg-[#04A05C]" data-id="element-869" data-mp="35cy69frc4ads">
            {t('faq.textUs')}
          </a>
        </div>
      </div>
    </section>;
}